import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import config from '@/config'
import { confirm } from '@/components/dialog'
import { useListingData } from './listing-data'
import { showMarkAsRentedModal } from '../../mark-as-rented'
import { useListingDashboardModals } from '../modals'

const { WEB_URL } = config

export const useListingActions = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { showModal } = useListingDashboardModals()
  const { listingId, isImported } = useListingData()
  const publicLink = `${WEB_URL}/rental-listings/${listingId}`

  const toggleHidden = async (is_hidden: 0 | 1) => {
    await dispatch.listing.update({
      listing_id: listingId,
      is_hidden
    })
    queryClient.invalidateQueries({ queryKey: ['listing-info', listingId] })
    queryClient.invalidateQueries({ queryKey: ['sponsored-listings'] })
    queryClient.invalidateQueries({ queryKey: ['my-listings', 'livrent'], exact: true })
  }

  const unhideListing = () =>
    isImported ? showModal('editListingOutsideModal')() : toggleHidden(0)
  const hideListing = () => (isImported ? showModal('editListingOutsideModal')() : toggleHidden(1))

  const markAsRented = () =>
    isImported
      ? showModal('editListingOutsideModal')()
      : showMarkAsRentedModal({
          handleInternal: () => history.push(`/listing/${listingId}/mark-rented`),
          listingId,
          history
        })

  const confirmDeletion = async () => {
    await confirm(
      t('l.delete_listing_modal.edit.delete_listing.title', 'Delete Listing?'),
      t(
        'l.delete_listing_modal.edit.confirmation.text',
        'Are you sure you want to delete this listing?'
      )
    )

    const { response } = await dispatch.listing.delete(listingId)
    if (response.ok) {
      history.replace('/listings')
    }
  }

  const editListing = () =>
    isImported ? showModal('editListingOutsideModal')() : history.push(`/listing/edit/${listingId}`)

  return {
    unhideListing,
    hideListing,
    markAsRented,
    confirmDeletion,
    publicLink,
    editListing,
    goToVerification: () => history.push(`/listing/verify/${listingId}`),
    goToPublicLink: () => window.open(publicLink, '_blank'),
    goToInsights: () => history.push(`/listing/${listingId}/insights`),
    goToChat: () => history.push(`/listing/${listingId}/chat?listing_id=${listingId}`),
    goToShowings: () => history.push(`/listing/${listingId}/showings`),
    goToNotes: () => history.push(`/listing/${listingId}/notes`),
    goToApplications: () => history.push(`/listing/${listingId}/applications`),
    goToSponsored: () => history.push(`/listing/${listingId}/sponsored`),
    goToSuggestedTenants: () => history.push(`/listing/${listingId}/suggested-tenants`),
    goToContract: () => history.push(`/listing/${listingId}/contract/create`),
    goToAutoReply: () => history.push('/settings/auto-reply')
  }
}
